import type { ApiClient } from './ApiClient';
import type { ParentPaymentHistoryReceipt } from './receipt';

// Forme alignée sur GET /parents/students/:studentId/financial-overview (PAY-01,
// apps/api/src/routes/parentPayments.ts) — seule source de vérité pour cette forme.
// Montants en CHAÎNE (jamais `number`), même prudence que tous les autres montants
// monétaires de ce projet (voir financialDashboard.ts/payment.ts/supplyOrder.ts).

// Mêmes 5 valeurs réelles de `FeeType` + `SUPPLIES` que
// `financialDashboard.ts`/DSH-02 — ⚠️ DIFFÉRENCE DÉLIBÉRÉE avec ce fichier : ici,
// SEULES les catégories avec AU MOINS UNE donnée réelle apparaissent (jamais les 6
// systématiquement) — vue personnelle d'un parent, pas un instrument de pilotage
// administratif, voir apps/api/src/lib/parentFinancialOverview.ts pour le détail
// complet de cette décision.
export type ParentFinancialCategory = 'TUITION' | 'REGISTRATION' | 'CANTEEN' | 'TRANSPORT' | 'OTHER' | 'SUPPLIES';

export interface ParentFinancialCategoryBreakdown {
  category: ParentFinancialCategory;
  due: string;
  paid: string;
  outstanding: string;
}

// Même forme que `InvoiceDue` (payment.ts) — délibérément dupliquée plutôt
// qu'importée : `payment.ts` reste le contrat de `GET /students/:id/invoices`
// (PAY-03, admin), une route DIFFÉRENTE avec une sémantique de filtre différente
// (factures NON soldées uniquement) — voir le commentaire de choix d'endpoint dans
// apps/api/src/routes/parentPayments.ts.
// grossAmountDue (PAY-10, mergé après l'écriture initiale de ce ticket) : vient
// gratuitement via selectInvoiceRecap() côté backend, toujours présent, égal à
// amountDue tant qu'aucune réduction n'est active sur l'échéance — voir
// docs/architecture.md, section "PAY-01", pour la note de correctif post-merge.
// PAY-20 : `receipt` — MÊME forme que `ParentPaymentHistoryReceipt` (RCP-04,
// receipt.ts), reçu du paiement COMPLETED le plus récent de cette facture,
// `null` tant qu'aucun n'existe — voir apps/api/src/lib/parentFinancialOverview.ts
// pour le détail complet (cas limite plusieurs paiements partiels).
export interface ParentInvoiceLine {
  id: string;
  // PAY-19 : numéro de facture lisible — vient gratuitement via
  // selectInvoiceRecap() côté backend, même remarque que grossAmountDue
  // ci-dessus.
  invoiceNumber: string | null;
  feeType: string;
  period: string | null;
  grossAmountDue: string;
  amountDue: string;
  amountPaid: string;
  balance: string;
  dueDate: string;
  status: string;
  receipt: ParentPaymentHistoryReceipt | null;
}

export interface ParentSupplyOrderItem {
  id: string;
  name: string;
  price: string;
  level: string;
}

export interface ParentSupplyOrderLine {
  id: string;
  status: string;
  totalAmount: string;
  method: string | null;
  paidAt: string | null;
  createdAt: string;
  items: ParentSupplyOrderItem[];
  // PAY-20 : reçu direct (relation 1-1 SupplyOrder -> Receipt, RCP-06), même
  // forme que ci-dessus.
  receipt: ParentPaymentHistoryReceipt | null;
}

export interface ParentStudentFinancialOverview {
  student: {
    id: string;
    firstName: string;
    lastName: string;
    matricule: string;
    schoolName: string;
    currentClassName: string | null;
    currency: string;
  };
  totalDue: string;
  totalPaid: string;
  totalOutstanding: string;
  // PAY-24 : somme des avoirs (PAY-08) réellement appliqués sur les factures de
  // cet enfant — purement informatif, `totalPaid`/`totalOutstanding` sont DÉJÀ
  // nets (voir apps/api/src/lib/parentFinancialOverview.ts).
  adjustmentsTotal: string;
  categoryBreakdown: ParentFinancialCategoryBreakdown[];
  invoices: ParentInvoiceLine[];
  supplyOrders: ParentSupplyOrderLine[];
}

export function getParentStudentFinancialOverview(
  client: ApiClient,
  studentId: string,
  accessToken: string,
): Promise<ParentStudentFinancialOverview> {
  return client.get<ParentStudentFinancialOverview>(`/parents/students/${studentId}/financial-overview`, { accessToken });
}

// PAR-05 : vue d'ensemble AGRÉGÉE des dettes de TOUS les enfants du parent, quel que
// soit leur tenant — `children` réutilise EXACTEMENT la forme `ParentStudentFinancialOverview`
// ci-dessus (PAY-01), un élément par (enfant déclaré, tenant) où un `Student` réel
// existe. `truncated` : cas limite de performance documenté côté serveur (voir
// apps/api/src/lib/parentFinancialOverview.ts) — `true` si le parent a plus de
// `DeclaredChild` que la limite défensive appliquée côté serveur (20).
export interface AggregatedParentFinancialOverview {
  totalDue: string;
  totalPaid: string;
  totalOutstanding: string;
  // PAY-24 : somme des `adjustmentsTotal` de chaque enfant.
  adjustmentsTotal: string;
  children: ParentStudentFinancialOverview[];
  truncated: boolean;
}

export function getAggregatedParentFinancialOverview(
  client: ApiClient,
  accessToken: string,
): Promise<AggregatedParentFinancialOverview> {
  return client.get<AggregatedParentFinancialOverview>('/parents/financial-overview', { accessToken });
}
